// ── Jobber → Koredor Job Mapper ──
// Pure mapping layer. Supports two input shapes:
//   1. CSV row (Record<string, string>) — primary beta path
//   2. Jobber-shaped JSON object — advanced/admin path
//
// Output: Partial<Job> seeded with safe defaults + ImportSourceMeta.
// Jobs land at currentStage="Sale Closed", migrationStatus="needs_review",
// scopeLocked=false. They are INVISIBLE to operational surfaces until an
// operator approves them via the Migration Review Queue.
//
// This mapper NEVER mutates Job state and NEVER calls writers. Caller is
// responsible for assigning a tenant + id and pushing into AppDataContext.
//
// Anchor: mem://features/jobber-import-bridge (beta)

import type {
  Job,
  JobFinancials,
  ProjectType,
  ImportSourceMeta,
  ImportReadinessMode,
  SourceSystem,
  JobAddress,
} from "../types";
import type { ContractDocument } from "../quoteTypes";
import type {
  MigrationJsonProject,
  MigrationJsonShowerSystem,
  MigrationJsonInstallation,
  MigrationJsonPlumbing,
  MigrationJsonDoors,
  MigrationJsonFixtures,
  MigrationJsonVanity,
  MigrationJsonElectrical,
  MigrationJsonToilet,
  MigrationJsonFlooring,
  MigrationJsonScope,
  MigrationJsonScopeItem,
  MigrationJsonJobMetadata,
  MigrationJsonBriefSnapshot,
} from "./migrationTypes";
import { PROJECT_TYPES } from "../types";
import { computeReviewFlags } from "./reviewFlags";

function normalizeImportedDate(value: string | undefined | null): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

function makeFinancials(contractValue: number): JobFinancials {
  return {
    contractValue,
    estimatedMaterialCost: 0,
    estimatedLaborCost: 0,
    subcontractorPayout: 0,
    marginEstimate: 0,
    paymentStatus: "Not Invoiced",
    payoutStatus: "Not Submitted",
  };
}

export interface JobberCsvRow {
  [key: string]: string | undefined;
}

export interface JobberJsonClient {
  id?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  phone?: string;
  email?: string;
  primary_phone?: string;
  primary_email?: string;
}
export interface JobberJsonProperty {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}
export interface JobberJsonLineItem {
  name?: string;
  sku?: string;
  quantity?: number;
}

export interface MapperContext {
  importBatchId: string;
  importedAt?: string; // defaults to now
  sourceSystem?: SourceSystem; // defaults to "jobber"
}

const PROJECT_TYPE_LOOKUP: Record<string, ProjectType> = (() => {
  const m: Record<string, ProjectType> = {};
  for (const p of PROJECT_TYPES) m[p.toLowerCase()] = p;
  // Common Jobber aliases
  m["bathroom"] = "Full Bathroom Remodel";
  m["bath remodel"] = "Full Bathroom Remodel";
  m["shower"] = "Shower Remodel";
  m["tub conversion"] = "Tub to Shower Conversion";
  m["walk in tub"] = "Walk-in Tub Installation";
  m["windows"] = "Window Install";
  m["doors"] = "Door Install";
  return m;
})();

function normalizeProjectType(raw?: string): ProjectType | undefined {
  if (!raw) return undefined;
  const key = raw.trim().toLowerCase();
  if (!key) return undefined;
  return PROJECT_TYPE_LOOKUP[key];
}

function safeString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    const text = safeString(value);
    if (text) return text;
  }
  return "";
}

function extractQuantityFromItemName(name: string): { quantity: number; cleanedName: string } {
  if (!name) return { quantity: 1, cleanedName: name };
  const match = name.match(/^\s*\((\d+)\)\s*/);
  if (match) {
    const quantity = Number(match[1]);
    const cleanedName = name.slice(match[0].length);
    return { quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1, cleanedName };
  }
  return { quantity: 1, cleanedName: name };
}

function customFieldValue(
  fields: MigrationJsonProject["custom_fields"] | undefined,
  ...keys: string[]
): string {
  if (!fields) return "";
  const normalizedKeys = new Set(keys.map((key) => key.trim().toLowerCase()));

  if (Array.isArray(fields)) {
    for (const field of fields) {
      const candidateKey = firstString(field.key, field.name, field.label).toLowerCase();
      if (candidateKey && normalizedKeys.has(candidateKey)) {
        return safeString(field.value);
      }
    }
    return "";
  }

  for (const key of keys) {
    const value = safeString(fields[key]);
    if (value) return value;
  }
  return "";
}

function clientDisplayName(client?: JobberJsonClient): string {
  if (!client) return "";
  const name = firstString(client.name);
  if (name) return name;
  return [client.first_name, client.last_name].map((part) => safeString(part)).filter(Boolean).join(" ");
}

function buildImportedContractDocument(input: {
  externalProjectId: string;
  customerName: string;
  customerEmail: string;
  customerAddress: JobAddress;
  contractTotal: number;
  signatureDate?: string;
  importedAt?: string;
}): ContractDocument | undefined {
  const signedAt = firstString(input.signatureDate);
  if (!signedAt) return undefined;

  const createdAt = input.importedAt ?? new Date().toISOString();
  return {
    id: `import-contract-${normalizeJobNumberToken(input.externalProjectId) || "UNKNOWN"}`,
    quoteId: input.externalProjectId,
    customerName: input.customerName,
    customerEmail: input.customerEmail,
    customerAddress: [
      input.customerAddress.street,
      input.customerAddress.city,
      input.customerAddress.state,
      input.customerAddress.zip,
    ].map((part) => safeString(part)).filter(Boolean).join(", "),
    contractTotal: input.contractTotal,
    homeownerSignedAt: signedAt,
    termsAccepted: true,
    rescissionAcknowledged: true,
    status: "Signed",
    createdAt,
    approvedAt: signedAt,
    sentToCustomerAt: signedAt,
    dispositionNotes: "Imported signed contract metadata.",
  };
}

function safeAddress(input?: JobberJsonProperty | JobberCsvRow): JobAddress {
  const street =
    (input as JobberJsonProperty | undefined)?.street ??
    (input as JobberCsvRow | undefined)?.street ??
    (input as JobberCsvRow | undefined)?.address ??
    "";
  const city =
    (input as JobberJsonProperty | undefined)?.city ??
    (input as JobberCsvRow | undefined)?.city ??
    "";
  const state =
    (input as JobberJsonProperty | undefined)?.state ??
    (input as JobberCsvRow | undefined)?.state ??
    "FL";
  const zip =
    (input as JobberJsonProperty | undefined)?.zip ??
    (input as JobberCsvRow | undefined)?.zip ??
    "";
  return { street: String(street || ""), city: String(city || ""), state: String(state || "FL"), zip: String(zip || "") };
}

function baseJobShape(): Partial<Job> {
  return {
    secondaryContactName: "",
    secondaryContactPhone: "",
    secondaryContactEmail: "",
    assignedCrew: "",
    assignmentType: "Unassigned",
    timeSlot: "",
    permitRequired: false,
    permitStatus: "Not Required",
    orderStatus: "Not Ordered",
    materialsStatus: "Not Ready",
    dispatchStatus: "Not Ready",
    contractorAssignmentStatus: "",
    currentStage: "Sale Closed",
    notes: "",
    issueFlag: false,
    payoutAmount: 0,
    assignedSubcontractor: "",
    targetInstallDate: "",
    activityHistory: [],
    qaSubmitted: false,
    materialReadiness: "Not Ordered",
    materialReadinessNotes: "",
    missingItemsList: "",
    procurementStatus: "Not Ordered",
    warehouseStatus: "Not Received",
    branch: "",
    region: "Tampa",
    salesRep: "",
    closeoutStatus: "Not Started",
    finalDocumentationComplete: false,
    customerSignoff: false,
    closeoutNotes: "",
    rescissionStatus: "Not Applicable",
    intakeApproved: false,
    approvedForOrdering: false,
    scopeLocked: false,
  };
}

function normalizeJobNumberToken(raw: string): string {
  return raw
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildImportJobNumber(rawExternalProjectId: string): string {
  const token = normalizeJobNumberToken(rawExternalProjectId);
  const trailingDigits = token.match(/(\d+)$/)?.[1];
  if (trailingDigits) {
    return `JOB-${trailingDigits.padStart(3, "0")}`;
  }
  return `JOB-${token || "UNKNOWN"}`;
}

function buildSourceMeta(
  externalProjectId: string,
  externalCustomerId: string | undefined,
  ctx: MapperContext,
  confidence: number,
  rawSourceRef?: string,
): ImportSourceMeta {
  const sourceSystem = ctx.sourceSystem ?? "jobber";
  const readinessMode: ImportReadinessMode =
    sourceSystem === "koredor" ? "native_managed" : "external_managed";
  return {
    sourceSystem,
    readinessMode,
    externalProjectId,
    externalCustomerId,
    importedAt: ctx.importedAt ?? new Date().toISOString(),
    importBatchId: ctx.importBatchId,
    migrationStatus: "needs_review",
    reviewFlags: [],
    rawSourceRef,
    mappingConfidence: confidence,
  };
}

function uniqueMaterialLines(lines: Array<{ itemName: string; sku?: string; quantity?: number }>): Array<{ itemName: string; sku?: string; quantity?: number }> {
  const seen = new Set<string>();
  const result: Array<{ itemName: string; sku?: string; quantity?: number }> = [];
  for (const line of lines) {
    const key = `${(line.sku ?? "").trim()}|${line.itemName.trim()}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(line);
    }
  }
  return result;
}

/** CSV → Partial<Job>. Caller assigns id + tenantId + runs computeReviewFlags. */
export function mapJobberCsvRow(row: JobberCsvRow, ctx: MapperContext): Partial<Job> {
  const externalProjectId =
    row.project_id || row.id || row.jobber_id || `csv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const customerName = firstString(
    row.customer_name,
    row.client_name,
    row.client,
    row.name,
    [row.first_name, row.last_name].map((part) => safeString(part)).filter(Boolean).join(" "),
  );
  const phone = firstString(row.primary_phone, row.phone);
  const email = firstString(row.primary_email, row.email);
  const projectType = normalizeProjectType(row.install_type || row.project_type);
  const scope = (row.scope || row.description || row.title || "").trim();
  const notes = firstString(row.notes, row.note);
  const signatureDate = firstString(row.signature_date, row.signed_at);
  const startAt = normalizeImportedDate(row.start_at || row.scheduled_date || row.install_date || "");
  const endAt = normalizeImportedDate(row.end_at || row.scheduled_end_at || "");
  const contractor = firstString(row.contractor, row.sales_rep, row.assigned_rep);
  const quoteNumber = firstString(row.quote_number, row.quote_id);
  const total = Number(row.total || row.contract_value || 0) || 0;

  // Confidence: weighted on presence of structured fields.
  let conf = 0.3;
  if (customerName) conf += 0.1;
  if (row.street || row.address) conf += 0.15;
  if (projectType) conf += 0.2;
  if (scope.length > 8) conf += 0.1;
  if (total > 0) conf += 0.1;
  if (startAt) conf += 0.05;
  conf = Math.min(1, conf);

  const sourceMeta = buildSourceMeta(
    String(externalProjectId),
    row.client_id,
    ctx,
    conf,
    row.source_url,
  );
  const importJobNumber = buildImportJobNumber(String(externalProjectId || ""));
  const address = safeAddress(row);

  const partial: Partial<Job> = {
    ...baseJobShape(),
    jobNumber: importJobNumber,
    customerName,
    address,
    phone,
    email,
    projectType: projectType ?? ("Other" as ProjectType),
    scopeSummary: scope,
    notes,
    scheduledInstallDate: startAt,
    targetInstallDate: endAt || undefined,
    salesRep: contractor || undefined,
    photos: [],
    financials: total > 0 ? makeFinancials(total) : undefined,
    contractDocument: buildImportedContractDocument({
      externalProjectId: quoteNumber || String(externalProjectId),
      customerName,
      customerEmail: email,
      customerAddress: address,
      contractTotal: total,
      signatureDate,
      importedAt: ctx.importedAt,
    }),
    sourceMeta,
  };
  partial.sourceMeta!.reviewFlags = computeReviewFlags(partial);
  return partial;
}

/** JSON → Partial<Job>. */
export function mapJobberJsonProject(project: MigrationJsonProject, ctx: MapperContext): Partial<Job> {
  const customInstallType = customFieldValue(project.custom_fields, "install_type", "project_type");
  const nestedType = safeString(project.project?.type);
  const topLevelType = safeString(project.project_type);
  const projectType = normalizeProjectType(customInstallType || nestedType || topLevelType);
  const scope = project.scope_summary || [project.title, project.description].filter(Boolean).join(" — ").trim();
  const notes = firstString(project.notes, project.note, customFieldValue(project.custom_fields, "notes", "note"));
  const signatureDate = firstString(
    project.signature_date,
    project.signed_at,
    customFieldValue(project.custom_fields, "signature_date", "signed_at"),
  );
  const total = Number(project.total ?? 0) || 0;
  const customerName = clientDisplayName(project.client);
  const phone = firstString(project.client?.primary_phone, project.client?.phone);
  const email = firstString(project.client?.primary_email, project.client?.email);
  const address = safeAddress(project.property);

  let conf = 0.4;
  if (customerName) conf += 0.1;
  if (project.property?.street) conf += 0.15;
  if (projectType) conf += 0.2;
  if (scope.length > 8) conf += 0.1;
  if (total > 0) conf += 0.1;
  if (project.start_at) conf += 0.05;
  conf = Math.min(1, conf);

  const sourceMeta = buildSourceMeta(
    project.id,
    project.client?.id,
    ctx,
    conf,
  );
  const importJobNumber = buildImportJobNumber(String(project.id || ""));

  // ── Materials: merge line_items + job_metadata.materials + scope includes ──
  const materialTextLines: Array<{ itemName: string; sku?: string; quantity?: number }> = [];

  const addMaterialText = (text: string | undefined, sku?: string, quantity?: number) => {
    const cleaned = String(text ?? "").trim();
    if (cleaned) {
      const { quantity: extractedQty, cleanedName } = extractQuantityFromItemName(cleaned);
      materialTextLines.push({ itemName: cleanedName, sku, quantity: quantity ?? extractedQty });
    }
  };

  for (const li of project.line_items ?? []) {
    addMaterialText(li.name, li.sku, li.quantity);
  }
  for (const text of project.job_metadata?.materials ?? []) {
    addMaterialText(text);
  }
  for (const text of project.brief_snapshot?.materials ?? []) {
    addMaterialText(text);
  }
  const scopeIncludes = project.scope?.shower_system?.includes ?? [];
  for (const text of scopeIncludes) {
    addMaterialText(text);
  }

  const uniqueMaterials = uniqueMaterialLines(materialTextLines);
  const materialsList: NonNullable<Job["materialsList"]> = uniqueMaterials.map((m) => ({
    sku: (m.sku ?? "").trim(),
    itemName: m.itemName,
    quantityRequired: m.quantity ?? 1,
    quantityAllocated: 0,
    quantityStaged: 0,
    quantityInstalled: 0,
    missingItems: 0,
    fieldPurchase: false,
    inventoryStatus: "In Stock",
    inventoryAssignment: "Assigned",
    lifecycleStage: "Ordered",
  }));

  // ── Photos: merge attachments + job_metadata.photos + brief_snapshot.photos ──
  const photoSet = new Set<string>();
  const photos: string[] = [];
  const addPhoto = (p: string | undefined) => {
    const cleaned = String(p ?? "").trim();
    if (cleaned && !photoSet.has(cleaned)) {
      photoSet.add(cleaned);
      photos.push(cleaned);
    }
  };
  for (const a of project.attachments ?? []) {
    addPhoto(a.url);
  }
  for (const p of project.job_metadata?.photos ?? []) {
    addPhoto(p);
  }
  for (const p of project.brief_snapshot?.photos ?? []) {
    addPhoto(p);
  }

  // ── Enhanced notes: combine top-level notes + special_notes ──
  const specialNotes = (project.job_metadata?.special_notes ?? []).filter(Boolean);
  const combinedNotes = [notes, ...specialNotes].filter(Boolean).join("\n");

  // ── Dates: fallback to schedule object ──
  const startAt = normalizeImportedDate(
    project.start_at ?? project.scheduled_date ?? project.schedule?.start_date ?? "",
  );
  const endAt = normalizeImportedDate(
    project.end_at ?? project.scheduled_end_at ?? project.schedule?.end_date ?? "",
  );

  const partial: Partial<Job> = {
    ...baseJobShape(),
    jobNumber: importJobNumber,
    customerName,
    address,
    phone,
    email,
    projectType: projectType ?? ("Other" as ProjectType),
    description: firstString(project.description) || undefined,
    scopeSummary: scope,
    notes: combinedNotes || undefined,
    scheduledInstallDate: startAt,
    targetInstallDate: endAt,
    photos,
    materialsList: materialsList.length > 0 ? materialsList : undefined,
    financials: total > 0 ? makeFinancials(total) : undefined,
    contractDocument: buildImportedContractDocument({
      externalProjectId: project.id,
      customerName,
      customerEmail: email,
      customerAddress: address,
      contractTotal: total,
      signatureDate,
      importedAt: ctx.importedAt,
    }),
    sourceMeta,
    ...(project.project?.contractor ? { salesRep: project.project.contractor } : {}),
  };

  if (project.project_subtype) {
    const existingConfig = (partial.projectConfig ?? {}) as Record<string, unknown>;
    (partial as Record<string, unknown>).projectConfig = {
      ...existingConfig,
      projectCategory: project.project_subtype,
    } as Job["projectConfig"];
  }

  // Attach rich migration metadata to the partial so it survives staging/approval
  const migrationMetadata: Record<string, unknown> = {};

  if (project.project?.contractor) {
    migrationMetadata.contractor = project.project.contractor;
  }
  if (project.project_subtype) {
    migrationMetadata.projectSubtype = project.project_subtype;
  }
  if (project.shower_system) {
    migrationMetadata.showerSystem = project.shower_system;
  }
  if (project.installation) {
    migrationMetadata.installation = project.installation;
  }
  if (project.plumbing) {
    migrationMetadata.plumbing = project.plumbing;
  }
  if (project.doors) {
    migrationMetadata.doors = project.doors;
  }
  if (project.fixtures) {
    migrationMetadata.fixtures = project.fixtures;
  }
  if (project.vanity) {
    migrationMetadata.vanity = project.vanity;
  }
  if (project.electrical_and_ceiling) {
    migrationMetadata.electrical = project.electrical_and_ceiling;
  }
  if (project.toilet) {
    migrationMetadata.toilet = project.toilet;
  }
  if (project.flooring) {
    migrationMetadata.flooring = project.flooring;
  }
  if (project.scope) {
    migrationMetadata.scope = project.scope;
  }
  if (project.job_metadata) {
    migrationMetadata.jobMetadata = project.job_metadata;
  }
  if (project.brief_snapshot) {
    migrationMetadata.briefSnapshot = project.brief_snapshot;
  }
  if (project.schedule) {
    migrationMetadata.schedule = project.schedule;
  }

  // Flatten key scope work items for easier UI access
  const scopeWorkItems: string[] = [];
  if (project.scope?.shower_installation?.work) scopeWorkItems.push(...project.scope.shower_installation.work);
  if (project.scope?.plumbing?.work) scopeWorkItems.push(...project.scope.plumbing.work);
  if (project.scope?.lighting_and_ceiling_work?.work) scopeWorkItems.push(...project.scope.lighting_and_ceiling_work.work);
  if (scopeWorkItems.length > 0) {
    migrationMetadata.scopeWorkItems = scopeWorkItems;
  }

  // Flatten expectations
  if (project.job_metadata?.expectations) {
    migrationMetadata.expectations = project.job_metadata.expectations;
  }
  if (project.job_metadata?.trades) {
    migrationMetadata.tradesInfo = project.job_metadata.trades;
  }

  // Attach metadata to partial via the metadata field
  const existingMetadata = (partial as Record<string, unknown>).metadata as Record<string, unknown> | undefined;
  (partial as Record<string, unknown>).metadata = { ...(existingMetadata ?? {}), ...migrationMetadata };

  // Also set migrationImportDetail directly for direct UI/test access
  (partial as Record<string, unknown>).migrationImportDetail = migrationMetadata;

  partial.sourceMeta!.reviewFlags = computeReviewFlags(partial);
  return partial;
}

// ── Lightweight CSV parser (handles quoted commas, no external dep). ──
export function parseCsv(text: string): JobberCsvRow[] {
  const lines: string[][] = [];
  let cur: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else { cell += c; }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { cur.push(cell); cell = ""; }
      else if (c === "\n" || c === "\r") {
        if (c === "\r" && text[i + 1] === "\n") i++;
        cur.push(cell); cell = "";
        if (cur.some((x) => x.length > 0)) lines.push(cur);
        cur = [];
      } else { cell += c; }
    }
  }
  if (cell.length > 0 || cur.length > 0) { cur.push(cell); if (cur.some((x) => x.length > 0)) lines.push(cur); }
  if (lines.length === 0) return [];
  const header = lines[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  return lines.slice(1).map((row) => {
    const r: JobberCsvRow = {};
    header.forEach((h, idx) => { r[h] = (row[idx] ?? "").trim(); });
    return r;
  });
}
